import { useRef, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  MapPin,
  Navigation,
  Phone,
  Send,
  User,
  Users,
} from "lucide-react";
import { WA_LINK, useLang } from "../i18n";
import { api } from "../lib/api";
import { Eyebrow, Reveal } from "./Reveal";

const EMPTY = {
  name: "",
  phone: "",
  pickup: "",
  destination: "",
  date: "",
  time: "",
  passengers: "1",
  notes: "",
};

export default function BookTaxi() {
  const { t, lang } = useLang();
  const formRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const buildMessage = () => {
    if (lang === "de") {
      return (
        `Hallo Singh Taxi! Ich möchte gerne ein Taxi buchen.\n\n` +
        `Name: ${form.name}\nTelefon: ${form.phone}\nAbholort: ${form.pickup}\n` +
        `Ziel: ${form.destination}\nDatum: ${form.date}\nUhrzeit: ${form.time}\n` +
        `Passagiere: ${form.passengers}\nAnmerkungen: ${form.notes}`
      );
    }
    return (
      `Hello Singh Taxi! I would like to book a taxi.\n\n` +
      `Name: ${form.name}\nPhone: ${form.phone}\nPickup: ${form.pickup}\n` +
      `Destination: ${form.destination}\nDate: ${form.date}\nTime: ${form.time}\n` +
      `Passengers: ${form.passengers}\nNotes: ${form.notes}`
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/bookings", { ...form, passengers: Number(form.passengers), lang });
      setForm(EMPTY);
      setState("success");
    } catch {
      setState("error");
    }
  };

  const openWhatsApp = (e) => {
    if (!formRef.current.reportValidity()) e.preventDefault();
  };

  const textFields = [
    { key: "name", label: t.book.fName, ph: t.book.phName, icon: User, required: true, type: "text" },
    { key: "phone", label: t.book.fPhone, ph: t.book.phPhone, icon: Phone, required: true, type: "tel" },
    { key: "pickup", label: t.book.fPickup, ph: t.book.phPickup, icon: MapPin, required: true, type: "text" },
    { key: "destination", label: t.book.fDest, ph: t.book.phDest, icon: Navigation, required: true, type: "text" },
    { key: "date", label: t.book.fDate, ph: "", icon: Calendar, required: false, type: "date" },
    { key: "time", label: t.book.fTime, ph: "", icon: Clock, required: false, type: "time" },
  ];

  const inputCls =
    "w-full rounded-xl border border-black/15 bg-white px-4 py-3.5 text-[15px] text-[#1a1a1a] outline-none transition placeholder:text-gray-400 focus:border-gold focus:ring-2 focus:ring-gold/30";

  const labelCls = "mb-2 flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.2em] text-[#333]";

  return (
    <section id="book" className="bg-cream py-24 lg:py-28">
      <div className="mx-auto max-w-4xl px-5 lg:px-10">
        <Reveal>
          <Eyebrow>{t.book.eyebrow}</Eyebrow>
          <h2
            data-testid="book-heading"
            className="mt-5 max-w-xl font-serif text-4xl text-[#141414] sm:text-5xl"
          >
            {t.book.title}
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          {state === "success" ? (
            <div
              data-testid="booking-success"
              className="mt-10 rounded-2xl border border-black/5 bg-white p-8 text-center shadow-sm sm:p-12"
            >
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
              <h3 className="mt-5 font-serif text-3xl text-[#141414]">{t.book.successTitle}</h3>
              <p className="mx-auto mt-3 max-w-md text-[15px] text-gray-600">{t.book.successText}</p>
              <button
                data-testid="booking-another-button"
                onClick={() => setState("idle")}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 text-sm font-extrabold text-[#1a1a1a] transition-all hover:-translate-y-0.5 hover:border-black/30"
              >
                {t.book.another}
              </button>
            </div>
          ) : (
          <form
            ref={formRef}
            data-testid="booking-form"
            onSubmit={submit}
            className="mt-10 rounded-2xl border border-black/5 bg-white p-6 shadow-sm sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              {textFields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls} htmlFor={`book-${f.key}`}>
                    <f.icon className="h-3.5 w-3.5 text-gold" />
                    {f.label}
                    {f.required && <span className="text-gold">*</span>}
                  </label>
                  <input
                    data-testid={`booking-input-${f.key}`}
                    id={`book-${f.key}`}
                    type={f.type}
                    required={f.required}
                    placeholder={f.ph}
                    value={form[f.key]}
                    onChange={update(f.key)}
                    className={inputCls}
                  />
                </div>
              ))}

              <div>
                <label className={labelCls} htmlFor="book-passengers">
                  <Users className="h-3.5 w-3.5 text-gold" />
                  {t.book.fPass}
                </label>
                <select
                  data-testid="booking-input-passengers"
                  id="book-passengers"
                  value={form.passengers}
                  onChange={update("passengers")}
                  className={inputCls}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="book-notes">
                  <FileText className="h-3.5 w-3.5 text-gold" />
                  {t.book.fDesc}
                </label>
                <textarea
                  data-testid="booking-input-notes"
                  id="book-notes"
                  rows={4}
                  placeholder={t.book.phDesc}
                  value={form.notes}
                  onChange={update("notes")}
                  className={`${inputCls} resize-y`}
                />
              </div>
            </div>

            {state === "error" && (
              <p data-testid="booking-error" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {t.book.error}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={state === "sending"}
                className="inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-base font-extrabold text-black shadow-[0_10px_30px_rgba(217,165,68,0.35)] transition-all hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-70"
              >
                {state === "sending" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {state === "sending" ? t.book.sending : t.book.submit}
              </button>
              <a
                data-testid="booking-whatsapp-button"
                href={`${WA_LINK}?text=${encodeURIComponent(buildMessage())}`}
                onClick={openWhatsApp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-full border border-black/15 bg-white px-8 py-4 text-base font-extrabold text-[#1a1a1a] transition-all hover:-translate-y-0.5 hover:border-black/30"
              >
                {t.book.wa}
              </a>
            </div>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
